import React, { useContext, useEffect, useState } from 'react';
import { Box, TextField } from '@material-ui/core';
import { HelpOutline } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import PropTypes from 'prop-types';

import Button from '~/components/Button';
import QuickBookRowAutocomplete from '~/components/QuickBookRow/QuickBookRowAutocomplete';
import QuickBookRowNotesAndAttachment from '~/components/QuickBookRow/QuickBookRowNotesAndAttachment';
import QuickBookRowScopeSelect from '~/components/QuickBookRow/QuickBookRowScopeSelect';
import {
  setInitialJournalScopeBasedOnAccount,
  setUpJournalForSubmit,
} from '~/components/QuickBookRow/transactionTemplates/helpers/journalFunctions';
import { useStyles } from '~/components/QuickBookRow/transactionTemplates/styles';
import { getManualBasicJournal } from '~/components/SearchSelect/TransactionTypeOptions';
import { PersonContext } from '~/contexts/PersonContext';
import { create } from '~/feathersFunctionalWrapper';
import { checkIsSmallScreen } from '~/functions/ScreenSizeFunctions';
import { useSegmentTrack } from '~/functions/SegmentFunctions';
import { getAccountOptions } from '~/helpers/utils/functionalAutocompleteLibrary';

import QuickBookRowLoading from '../QuickBookRowLoading';

export default function QuickBookRowSecurityDepositReturnTemplate({ transaction, onTransactionAction, onGetHelp }) {
  const isMobile = checkIsSmallScreen();
  const tracking = useSegmentTrack();
  const { organizationId } = useContext(PersonContext);
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(true);
  const [securityDepositAccountOptions, setSecurityDepositAccountOptions] = useState([]);
  const [journal, setJournal] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isLoading) {
      return;
    }

    const getData = async () => {
      const securityDepositAccounts = await getAccountOptions(organizationId, {
        type2: 'Security Deposits',
        inactive: { $or: [null, false] },
      });
      setSecurityDepositAccountOptions(securityDepositAccounts);

      const journalObject = getManualBasicJournal();
      const { account, id, description, date, amount } = transaction;
      journalObject.creditLines[0].accountIdSelect = account;
      journalObject.creditLines[0].transactionId = id;
      if (securityDepositAccounts.length === 1) {
        journalObject.debitLines[0].accountIdSelect = securityDepositAccounts[0];
      }
      journalObject.description = description;
      journalObject.date = date;
      journalObject.amount = amount;
      await setInitialJournalScopeBasedOnAccount(transaction.account, journalObject);
      setJournal(journalObject);
      setIsLoading(false);
    };
    getData();
  }, [isLoading, organizationId, transaction]);

  const addSecurityDepositReturn = async (event) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    const journalData = {
      transactionId: transaction.id,
      journal,
      organizationId,
      type: 'Security Deposit Out',
      direction: 'cash out',
      bookAsRefund: true,
    };
    const journalSubmit = setUpJournalForSubmit(journalData);

    try {
      await create('journals', journalSubmit);
      setIsSubmitting(false);
      onTransactionAction();
    } catch (err) {
      setError(err);
      setIsSubmitting(false);
      return;
    }
  };

  if (isLoading) {
    return <QuickBookRowLoading />;
  }

  return (
    <form onSubmit={addSecurityDepositReturn}>
      <Box className={classes.rootContainer}>
        <Box className={classes.container}>
          <QuickBookRowAutocomplete
            className={classes.autoCompleteField}
            displaySubaccounts
            options={securityDepositAccountOptions}
            onChange={(value) => {
              setJournal({ ...journal, debitLines: [{ ...journal.debitLines[0], accountIdSelect: value }] });
            }}
            label="Security Deposit Account"
            value={journal.debitLines[0]?.accountIdSelect}
            required
            getOptionLabel={(option) => option.name}
          />
          <TextField
            className={isMobile ? classes.mobileDescriptionField : classes.descriptionFieldSixtySixPercent}
            label="Description"
            placeholder="Description"
            variant="outlined"
            size="small"
            value={journal.description}
            onChange={(event) => {
              setJournal({ ...journal, description: event.target.value });
            }}
          />
        </Box>
        <QuickBookRowScopeSelect journal={journal} setJournal={setJournal} />
        <QuickBookRowNotesAndAttachment
          journal={journal}
          onChange={(newJournal) => {
            setJournal(newJournal);
          }}
        />
        {error && error.message ? (
          <Alert severity="error" className={classes.errorAlert}>
            {error.message}
          </Alert>
        ) : null}
        <Box className={classes.actionsContainer}>
          <Button
            variant="outlined"
            size="small"
            className={classes.helpButton}
            onClick={() => {
              tracking('get_help clicked', { location: 'Inline Booking - Import Feed' });
              onGetHelp();
            }}
          >
            <HelpOutline className={classes.helpIcon} />
            Get Help
          </Button>
          <Box className={classes.rowActionsSecondaryContainer}>
            <Button
              variant="contained"
              className={classes.saveButton}
              size="small"
              type="submit"
              onClick={() => {
                tracking('save clicked', { location: 'import_feed rei' });
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Box>
    </form>
  );
}

QuickBookRowSecurityDepositReturnTemplate.propTypes = {
  transaction: PropTypes.object.isRequired,
  onTransactionAction: PropTypes.func,
  onGetHelp: PropTypes.func.isRequired,
};
